// src/components/admin_panel/AdminEmailCampaigns.js
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';

/**
 * AdminEmailCampaigns
 *
 * Manages email campaigns with:
 * - Campaign list with enable/disable toggles
 * - Inline analytics (sends, open rate, click rate, bounce rate)
 * - Create/edit campaign modal
 * - Delete confirmation
 * - Audience preview
 */
function AdminEmailCampaigns({ adminToken }) {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignAnalytics, setCampaignAnalytics] = useState({}); // Store analytics by campaign ID
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    campaign_name: '',
    description: '',
    enabled: true,
    template_id: '',
    subject_line: '',
    schedule_cron: '',
    // User-friendly schedule fields
    schedule_type: 'manual', // 'manual', 'daily', 'weekly', 'custom'
    schedule_time: '09:00',   // HH:MM format
    schedule_days: [],        // Array of day numbers (0=Sunday, 1=Monday, ...)
    max_sends_per_run: 100,
    min_hours_since_signup: 1,
    min_hours_between_emails: 24,
    max_emails_per_week: 3,
    asm_group_id: 28561,
    // Audience filters
    marketing_opt_in: true,
    subscription_status: ['none'],
    age_min: '',
    age_max: 45,
    gender_ids: [],
    location_ids: []
  });

  // Fetch campaigns on mount
  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line
  }, [adminToken]);

  async function fetchCampaigns() {
    if (!adminToken) return;
    try {
      setLoading(true);
      const data = await adminApi.getEmailCampaigns(adminToken);
      setCampaigns(data);

      // Fetch analytics for all campaigns
      await fetchAllAnalytics(data);

      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllAnalytics(campaignsList) {
    const analyticsData = {};

    // Fetch analytics for each campaign in parallel
    await Promise.all(
      campaignsList.map(async (campaign) => {
        try {
          const analytics = await adminApi.getEmailCampaignAnalytics(campaign.id, adminToken);
          analyticsData[campaign.id] = analytics;
        } catch (err) {
          console.error(`Failed to fetch analytics for campaign ${campaign.id}:`, err);
          // Set default values on error
          analyticsData[campaign.id] = {
            total_sends: 0,
            open_rate: 0,
            click_rate: 0,
            bounce_rate: 0
          };
        }
      })
    );

    setCampaignAnalytics(analyticsData);
  }

  // Helper function: Parse cron expression into user-friendly format
  function parseCronToSchedule(cronExpression) {
    if (!cronExpression) {
      return { schedule_type: 'manual', schedule_time: '09:00', schedule_days: [] };
    }

    // Cron format: minute hour day month weekday
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length !== 5) {
      return { schedule_type: 'custom', schedule_time: '09:00', schedule_days: [] };
    }

    const [minute, hour, day, month, weekday] = parts;

    // Daily schedule: "0 9 * * *" (runs at 9:00 every day)
    if (day === '*' && month === '*' && weekday === '*') {
      return {
        schedule_type: 'daily',
        schedule_time: `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`,
        schedule_days: []
      };
    }

    // Weekly schedule: "0 9 * * 1,3,5" (runs at 9:00 on Mon, Wed, Fri)
    if (day === '*' && month === '*' && weekday !== '*') {
      const days = weekday.split(',').map(d => parseInt(d));
      return {
        schedule_type: 'weekly',
        schedule_time: `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`,
        schedule_days: days
      };
    }

    // Anything else is custom
    return { schedule_type: 'custom', schedule_time: '09:00', schedule_days: [] };
  }

  // Helper function: Convert user-friendly format to cron expression
  function buildCronFromSchedule(schedule_type, schedule_time, schedule_days) {
    if (schedule_type === 'manual') {
      return ''; // No schedule
    }

    // Parse time (HH:MM)
    const [hour, minute] = schedule_time.split(':').map(t => parseInt(t));

    if (schedule_type === 'daily') {
      // Daily at specified time: "0 9 * * *"
      return `${minute} ${hour} * * *`;
    }

    if (schedule_type === 'weekly') {
      // Weekly on specified days: "0 9 * * 1,3,5"
      if (!schedule_days || schedule_days.length === 0) {
        // Default to Monday if no days selected
        return `${minute} ${hour} * * 1`;
      }
      const dayString = schedule_days.sort((a, b) => a - b).join(',');
      return `${minute} ${hour} * * ${dayString}`;
    }

    // Custom - return the raw cron if available
    return '';
  }

  function openCreateModal() {
    setModalMode('create');
    setFormData({
      campaign_name: '',
      description: '',
      enabled: true,
      template_id: '',
      subject_line: '',
      schedule_cron: '',
      schedule_type: 'manual',
      schedule_time: '09:00',
      schedule_days: [],
      max_sends_per_run: 100,
      min_hours_since_signup: 1,
      min_hours_between_emails: 24,
      max_emails_per_week: 3,
      asm_group_id: 28561,
      marketing_opt_in: true,
      subscription_status: ['none'],
      age_min: '',
      age_max: 45,
      gender_ids: [],
      location_ids: []
    });
    setShowModal(true);
  }

  function openEditModal(campaign) {
    setModalMode('edit');
    setSelectedCampaign(campaign);

    // Parse audience filter
    const filter = campaign.audience_filter || {};

    // Parse cron expression into user-friendly format
    const scheduleData = parseCronToSchedule(campaign.schedule_cron);

    setFormData({
      campaign_name: campaign.campaign_name,
      description: campaign.description || '',
      enabled: campaign.enabled,
      template_id: campaign.template_id,
      subject_line: campaign.subject_line || '',
      schedule_cron: campaign.schedule_cron || '',
      schedule_type: scheduleData.schedule_type,
      schedule_time: scheduleData.schedule_time,
      schedule_days: scheduleData.schedule_days,
      max_sends_per_run: campaign.max_sends_per_run,
      min_hours_since_signup: campaign.min_hours_since_signup,
      min_hours_between_emails: campaign.min_hours_between_emails || 24,
      max_emails_per_week: campaign.max_emails_per_week || 3,
      asm_group_id: campaign.asm_group_id || 28561,
      marketing_opt_in: filter.marketing_opt_in !== undefined ? filter.marketing_opt_in : true,
      subscription_status: filter.subscription_status || ['none'],
      age_min: filter.age_min || '',
      age_max: filter.age_max || 45,
      gender_ids: filter.gender_ids || [],
      location_ids: filter.location_ids || []
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedCampaign(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setLoading(true);

      // Build audience filter from form data
      const audience_filter = {
        marketing_opt_in: formData.marketing_opt_in,
        subscription_status: formData.subscription_status
      };

      if (formData.age_min) audience_filter.age_min = parseInt(formData.age_min);
      if (formData.age_max) audience_filter.age_max = parseInt(formData.age_max);
      if (formData.gender_ids.length > 0) audience_filter.gender_ids = formData.gender_ids;
      if (formData.location_ids.length > 0) audience_filter.location_ids = formData.location_ids;

      // Build cron expression from user-friendly schedule fields
      let schedule_cron;
      if (formData.schedule_type === 'custom') {
        // Use raw cron expression for custom schedules
        schedule_cron = formData.schedule_cron;
      } else {
        // Build cron from user-friendly fields
        schedule_cron = buildCronFromSchedule(
          formData.schedule_type,
          formData.schedule_time,
          formData.schedule_days
        );
      }

      // Build campaign data
      const campaignData = {
        campaign_name: formData.campaign_name,
        description: formData.description,
        enabled: formData.enabled,
        template_id: formData.template_id,
        subject_line: formData.subject_line,
        schedule_cron: schedule_cron,
        audience_filter,
        max_sends_per_run: parseInt(formData.max_sends_per_run),
        min_hours_since_signup: parseInt(formData.min_hours_since_signup),
        min_hours_between_emails: parseInt(formData.min_hours_between_emails),
        max_emails_per_week: parseInt(formData.max_emails_per_week),
        asm_group_id: parseInt(formData.asm_group_id)
      };

      if (modalMode === 'create') {
        await adminApi.createEmailCampaign(campaignData, adminToken);
      } else {
        await adminApi.updateEmailCampaign(selectedCampaign.id, campaignData, adminToken);
      }

      await fetchCampaigns();
      closeModal();
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleEnabled(campaign) {
    try {
      await adminApi.updateEmailCampaign(campaign.id, { enabled: !campaign.enabled }, adminToken);
      await fetchCampaigns();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(campaignId) {
    try {
      setLoading(true);
      await adminApi.deleteEmailCampaign(campaignId, adminToken);
      await fetchCampaigns();
      setDeleteConfirm(null);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleViewPreview(campaign) {
    try {
      setLoading(true);
      const data = await adminApi.previewEmailCampaignAudience(campaign.id, adminToken);
      setPreview(data);
      setSelectedCampaign(campaign);
      setShowPreview(true);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleFormChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function handleSubscriptionStatusChange(status, checked) {
    setFormData(prev => {
      const current = prev.subscription_status || [];
      if (checked) {
        return { ...prev, subscription_status: [...current, status] };
      } else {
        return { ...prev, subscription_status: current.filter(s => s !== status) };
      }
    });
  }

  function handleScheduleDayToggle(dayNum, checked) {
    setFormData(prev => {
      const current = prev.schedule_days || [];
      if (checked) {
        return { ...prev, schedule_days: [...current, dayNum].sort((a, b) => a - b) };
      } else {
        return { ...prev, schedule_days: current.filter(d => d !== dayNum) };
      }
    });
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.8rem', fontWeight: '600' }}>
          Email Campaigns
        </h2>
        <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.95rem' }}>
          Manage automated email campaigns with audience targeting and analytics
        </p>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.6rem 1.2rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}
        >
          + Create Campaign
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24'
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && !showModal && !showPreview && (
        <p style={{ padding: '1rem', color: '#666', fontStyle: 'italic' }}>Loading...</p>
      )}

      {/* Campaigns Table */}
      {!loading && campaigns.length > 0 && (
        <div style={{ overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white'
          }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                <th style={headerStyle}>Campaign Name</th>
                <th style={headerStyle}>Description</th>
                <th style={headerStyle}>Status</th>
                <th style={headerStyle}>Template ID</th>
                <th style={headerStyle}>Max Sends</th>
                <th style={headerStyle}>Frequency Limits</th>
                <th style={headerStyle}>Total Sends</th>
                <th style={headerStyle}>Open Rate</th>
                <th style={headerStyle}>Click Rate</th>
                <th style={headerStyle}>Bounce Rate</th>
                <th style={headerStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign, idx) => (
                <tr
                  key={campaign.id}
                  style={{
                    borderBottom: '1px solid #e9ecef',
                    backgroundColor: idx % 2 === 0 ? 'white' : '#fafbfc'
                  }}
                >
                  <td style={cellStyle}>
                    <strong>{campaign.campaign_name}</strong>
                  </td>
                  <td style={cellStyle}>
                    {campaign.description || '--'}
                  </td>
                  <td style={cellStyle}>
                    <button
                      onClick={() => handleToggleEnabled(campaign)}
                      style={{
                        padding: '0.3rem 0.7rem',
                        backgroundColor: campaign.enabled ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      {campaign.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td style={cellStyle}>
                    <code style={{ fontSize: '0.8rem' }}>{campaign.template_id}</code>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {campaign.max_sends_per_run}
                  </td>
                  <td style={cellStyle}>
                    <div style={{ fontSize: '0.85rem' }}>
                      {campaign.min_hours_between_emails && (
                        <div>Min gap: {campaign.min_hours_between_emails}h</div>
                      )}
                      {campaign.max_emails_per_week && (
                        <div>Max/week: {campaign.max_emails_per_week}</div>
                      )}
                    </div>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {campaignAnalytics[campaign.id]?.total_sends ?? '--'}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {campaignAnalytics[campaign.id]?.open_rate !== undefined
                      ? `${campaignAnalytics[campaign.id].open_rate.toFixed(1)}%`
                      : '--'}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {campaignAnalytics[campaign.id]?.click_rate !== undefined
                      ? `${campaignAnalytics[campaign.id].click_rate.toFixed(1)}%`
                      : '--'}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {campaignAnalytics[campaign.id]?.bounce_rate !== undefined
                      ? `${campaignAnalytics[campaign.id].bounce_rate.toFixed(1)}%`
                      : '--'}
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openEditModal(campaign)}
                        style={actionButtonStyle('#ffc107', '#212529')}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewPreview(campaign)}
                        style={actionButtonStyle('#17a2b8', 'white')}
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(campaign)}
                        style={actionButtonStyle('#dc3545', 'white')}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && campaigns.length === 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <p style={{ margin: 0, color: '#6c757d', fontSize: '1rem' }}>
            No campaigns found. Create your first campaign to get started!
          </p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0 }}>
              {modalMode === 'create' ? 'Create Campaign' : 'Edit Campaign'}
            </h3>
            <form onSubmit={handleSubmit} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Basic Info */}
              <div style={sectionStyle}>
                <h4 style={sectionHeaderStyle}>Basic Information</h4>

                <label style={labelStyle}>
                  Campaign Name *
                  <input
                    type="text"
                    value={formData.campaign_name}
                    onChange={(e) => handleFormChange('campaign_name', e.target.value)}
                    required
                    style={inputStyle}
                    placeholder="e.g., welcome_email_1"
                  />
                </label>

                <label style={labelStyle}>
                  Description
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    style={{ ...inputStyle, minHeight: '60px' }}
                    placeholder="Brief description of this campaign"
                  />
                </label>

                <label style={labelStyle}>
                  SendGrid Template ID *
                  <input
                    type="text"
                    value={formData.template_id}
                    onChange={(e) => handleFormChange('template_id', e.target.value)}
                    required
                    style={inputStyle}
                    placeholder="d-xxxxxxxxxxxxxxxx"
                  />
                </label>

                <label style={labelStyle}>
                  Email Subject Line
                  <input
                    type="text"
                    value={formData.subject_line}
                    onChange={(e) => handleFormChange('subject_line', e.target.value)}
                    style={inputStyle}
                    placeholder="Welcome to OkGather!"
                  />
                </label>

                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => handleFormChange('enabled', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Campaign Enabled
                </label>
              </div>

              {/* Campaign Limits */}
              <div style={sectionStyle}>
                <h4 style={sectionHeaderStyle}>Campaign Limits</h4>

                <label style={labelStyle}>
                  Max Sends Per Run *
                  <input
                    type="number"
                    value={formData.max_sends_per_run}
                    onChange={(e) => handleFormChange('max_sends_per_run', e.target.value)}
                    required
                    min="1"
                    style={inputStyle}
                  />
                  <small style={{ color: '#666' }}>Maximum emails to send each time campaign runs</small>
                </label>

                <label style={labelStyle}>
                  Min Hours Since Signup *
                  <input
                    type="number"
                    value={formData.min_hours_since_signup}
                    onChange={(e) => handleFormChange('min_hours_since_signup', e.target.value)}
                    required
                    min="0"
                    style={inputStyle}
                  />
                  <small style={{ color: '#666' }}>Wait this many hours after signup before sending</small>
                </label>
              </div>

              {/* Frequency Limiting */}
              <div style={sectionStyle}>
                <h4 style={sectionHeaderStyle}>Frequency Limiting (Anti-Spam)</h4>

                <label style={labelStyle}>
                  Min Hours Between Emails
                  <input
                    type="number"
                    value={formData.min_hours_between_emails}
                    onChange={(e) => handleFormChange('min_hours_between_emails', e.target.value)}
                    min="0"
                    style={inputStyle}
                  />
                  <small style={{ color: '#666' }}>Don't send if user received ANY email within this many hours</small>
                </label>

                <label style={labelStyle}>
                  Max Emails Per Week
                  <input
                    type="number"
                    value={formData.max_emails_per_week}
                    onChange={(e) => handleFormChange('max_emails_per_week', e.target.value)}
                    min="0"
                    style={inputStyle}
                  />
                  <small style={{ color: '#666' }}>Maximum total emails user can receive in 7 days</small>
                </label>
              </div>

              {/* Audience Targeting */}
              <div style={sectionStyle}>
                <h4 style={sectionHeaderStyle}>Audience Targeting</h4>

                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    checked={formData.marketing_opt_in}
                    onChange={(e) => handleFormChange('marketing_opt_in', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Only users with marketing opt-in
                </label>

                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Subscription Status:</strong>
                  <label style={{ display: 'block', marginBottom: '0.3rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.subscription_status.includes('none')}
                      onChange={(e) => handleSubscriptionStatusChange('none', e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    No subscription
                  </label>
                  <label style={{ display: 'block', marginBottom: '0.3rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.subscription_status.includes('trialing')}
                      onChange={(e) => handleSubscriptionStatusChange('trialing', e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Trialing
                  </label>
                  <label style={{ display: 'block', marginBottom: '0.3rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.subscription_status.includes('active')}
                      onChange={(e) => handleSubscriptionStatusChange('active', e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Active subscription
                  </label>
                  <label style={{ display: 'block', marginBottom: '0.3rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.subscription_status.includes('cancelled')}
                      onChange={(e) => handleSubscriptionStatusChange('cancelled', e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Cancelled subscription
                  </label>
                </div>

                <label style={labelStyle}>
                  Min Age
                  <input
                    type="number"
                    value={formData.age_min}
                    onChange={(e) => handleFormChange('age_min', e.target.value)}
                    min="18"
                    max="100"
                    style={inputStyle}
                    placeholder="Leave empty for no minimum"
                  />
                </label>

                <label style={labelStyle}>
                  Max Age
                  <input
                    type="number"
                    value={formData.age_max}
                    onChange={(e) => handleFormChange('age_max', e.target.value)}
                    min="18"
                    max="100"
                    style={inputStyle}
                  />
                </label>
              </div>

              {/* Advanced Settings */}
              <div style={sectionStyle}>
                <h4 style={sectionHeaderStyle}>Campaign Schedule</h4>

                <label style={labelStyle}>
                  Schedule Type *
                  <select
                    value={formData.schedule_type}
                    onChange={(e) => handleFormChange('schedule_type', e.target.value)}
                    style={inputStyle}
                  >
                    <option value="manual">Manual (no automatic schedule)</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom">Custom (advanced)</option>
                  </select>
                  <small style={{ color: '#666' }}>
                    {formData.schedule_type === 'manual' && 'Campaign will only run when manually triggered'}
                    {formData.schedule_type === 'daily' && 'Campaign will run every day at the specified time'}
                    {formData.schedule_type === 'weekly' && 'Campaign will run on selected days at the specified time'}
                    {formData.schedule_type === 'custom' && 'Enter a custom cron expression for advanced scheduling'}
                  </small>
                </label>

                {formData.schedule_type !== 'manual' && formData.schedule_type !== 'custom' && (
                  <label style={labelStyle}>
                    Time (UTC) *
                    <input
                      type="time"
                      value={formData.schedule_time}
                      onChange={(e) => handleFormChange('schedule_time', e.target.value)}
                      style={inputStyle}
                    />
                    <small style={{ color: '#666' }}>All times are in UTC timezone</small>
                  </label>
                )}

                {formData.schedule_type === 'weekly' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Days of Week *</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                      {[
                        { num: 1, label: 'Monday' },
                        { num: 2, label: 'Tuesday' },
                        { num: 3, label: 'Wednesday' },
                        { num: 4, label: 'Thursday' },
                        { num: 5, label: 'Friday' },
                        { num: 6, label: 'Saturday' },
                        { num: 0, label: 'Sunday' }
                      ].map(day => (
                        <label key={day.num} style={{ display: 'block', marginBottom: '0.3rem' }}>
                          <input
                            type="checkbox"
                            checked={formData.schedule_days.includes(day.num)}
                            onChange={(e) => handleScheduleDayToggle(day.num, e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          {day.label}
                        </label>
                      ))}
                    </div>
                    {formData.schedule_days.length === 0 && (
                      <small style={{ color: '#d9534f' }}>Please select at least one day</small>
                    )}
                  </div>
                )}

                {formData.schedule_type === 'custom' && (
                  <label style={labelStyle}>
                    Cron Expression *
                    <input
                      type="text"
                      value={formData.schedule_cron}
                      onChange={(e) => handleFormChange('schedule_cron', e.target.value)}
                      style={inputStyle}
                      placeholder="0 9 * * * (9am daily)"
                    />
                    <small style={{ color: '#666' }}>
                      Standard cron format: minute hour day month weekday
                      <br />
                      Example: "0 9 * * 1,3,5" = 9am on Mon, Wed, Fri
                    </small>
                  </label>
                )}
              </div>

              {/* Advanced Settings */}
              <div style={sectionStyle}>
                <h4 style={sectionHeaderStyle}>Advanced Settings</h4>

                <label style={labelStyle}>
                  ASM Unsubscribe Group ID
                  <input
                    type="number"
                    value={formData.asm_group_id}
                    onChange={(e) => handleFormChange('asm_group_id', e.target.value)}
                    style={inputStyle}
                  />
                  <small style={{ color: '#666' }}>SendGrid unsubscribe group ID</small>
                </label>
              </div>

              {/* Form Actions */}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '0.6rem 1.2rem',
                    backgroundColor: loading ? '#9fcdae' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {loading ? 'Saving...' : modalMode === 'create' ? 'Create Campaign' : 'Update Campaign'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={loading}
                  style={{
                    padding: '0.6rem 1.2rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && preview && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0 }}>
              Audience Preview: {selectedCampaign?.campaign_name}
            </h3>

            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Total eligible users: <strong>{preview.total_eligible}</strong>
            </p>

            {preview.users && preview.users.length > 0 ? (
              <div>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                  Showing first {preview.preview_count} users:
                </p>
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th style={{ ...headerStyle, position: 'sticky', top: 0 }}>Email</th>
                        <th style={{ ...headerStyle, position: 'sticky', top: 0 }}>Name</th>
                        <th style={{ ...headerStyle, position: 'sticky', top: 0 }}>Signed Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.users.map((user, idx) => (
                        <tr key={user.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                          <td style={cellStyle}>{user.email}</td>
                          <td style={cellStyle}>{user.first_name} {user.last_name}</td>
                          <td style={cellStyle}>
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p style={{ color: '#666' }}>No eligible users found for this campaign.</p>
            )}

            <button
              onClick={() => setShowPreview(false)}
              style={{
                marginTop: '1rem',
                padding: '0.6rem 1.2rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '400px' }}>
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>Confirm Delete</h3>
            <p>
              Are you sure you want to delete the campaign <strong>{deleteConfirm.campaign_name}</strong>?
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              This will also delete all send logs and analytics data associated with this campaign.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={loading}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: loading ? '#f5c6cb' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={loading}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const headerStyle = {
  padding: '0.75rem',
  textAlign: 'left',
  fontWeight: '600',
  fontSize: '0.9rem',
  color: '#495057'
};

const cellStyle = {
  padding: '0.75rem',
  fontSize: '0.9rem',
  color: '#212529'
};

const actionButtonStyle = (bgColor, textColor) => ({
  padding: '0.4rem 0.8rem',
  backgroundColor: bgColor,
  color: textColor,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: '500'
});

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  overflowY: 'auto',
  padding: '2rem'
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '8px',
  maxWidth: '600px',
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const sectionStyle = {
  marginBottom: '1.5rem',
  paddingBottom: '1.5rem',
  borderBottom: '1px solid #e9ecef'
};

const sectionHeaderStyle = {
  marginTop: 0,
  marginBottom: '1rem',
  fontSize: '1.1rem',
  color: '#495057'
};

const labelStyle = {
  display: 'block',
  marginBottom: '1rem',
  fontSize: '0.9rem',
  fontWeight: '500',
  color: '#495057'
};

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '0.5rem',
  marginTop: '0.25rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.9rem',
  boxSizing: 'border-box'
};

export default AdminEmailCampaigns;
